/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.parallelcoordinates

import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.high_d.axis.Alignment
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.common.timer.CPTimer
import com.macrofocus.common.timer.CPTimerListener
import org.mkui.canvas.CPCanvas
import org.mkui.canvas.MouseEvent
import org.mkui.canvas.MouseListener
import org.mkui.canvas.MouseMotionListener
import org.mkui.color.MkColor
import org.mkui.color.colorOf
import org.mkui.font.CPFontFactory
import org.mkui.font.MkFont
import org.mkui.font.Weight
import org.mkui.geom.Point2D
import org.mkui.geom.Rectangle
import org.mkui.geom.Rectangle2D
import org.mkui.graphics.AbstractIDrawing
import org.mkui.graphics.IGraphics
import kotlin.math.abs

/**
 * The header of an axis of the parallel coordinates, after AxisHeaderView and PopupAxisController: a button with the
 * name of the axis that
 * - reorders the axes when dragged horizontally,
 * - inverts the axis on double-click (see [ParallelCoordinatesView.headerSelected]),
 * - opens the popup menu of the axes on a popup trigger, selecting the axis (its [AxisModel.buttonModel]),
 * - shows its full name in a tooltip when the mouse rests over it and the name does not fit.
 *
 * The mouse events it handles are not passed on to the listeners of the view.
 */
class AxisHeaderView<Row, Column>(
    private val view: DefaultParallelCoordinatesView<Row, Column>,
    val axisModel: AxisModel<Row, Column>,
) : CPCanvas() {
    private var pressed = false
    private var dragged = false

    /** Horizontal location of the press in the coordinates of the view, the header moving while dragged. */
    private var pressX = 0

    /** Whether the name was shortened to fit, and its full width, as last drawn. */
    private var elided = false
    private var nameWidth = 0.0

    /** Whether the mouse is over the header, and where, for the tooltip. */
    private var hovered = false
    private var hoverX = 0
    private var hoverY = 0

    private val tooltipTimer: CPTimer = CPHelper.instance.createTimer("AxisHeaderView\$Tooltip", TooltipView.DELAY, true, object : CPTimerListener {
        override fun timerTriggered() {
            if (hovered && !pressed && elided) {
                view.showTooltip(this@AxisHeaderView, axisModel.name, nameWidth, hoverX, hoverY)
            }
        }
    })

    /** Whether the header reacts to the mouse; the compact headers of the parallel coordinates matrix do not. */
    var isInteractive: Boolean = true

    /** Whether the axis name is centered, aligned on the left or on the right of the header. */
    var alignment: Alignment = Alignment.Center
        set(value) {
            if (field != value) {
                field = value
                drawing.changed()
            }
        }

    private val drawing = object : AbstractIDrawing() {
        override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
            if (width <= 0 || height <= 0) {
                return
            }
            val down = pressed || axisModel.buttonModel.value
            g.setColor(if (down) DOWN else FACE)
            g.fillRectangle(0, 0, width.toInt(), height.toInt())
            g.setColor(BORDER)
            g.setLineWidth(1.0)
            g.drawShape(Rectangle2D.Double(0.5, 0.5, width - 1, height - 1))

            g.setFont(FONT)
            g.setColor(TEXT)
            g.setTextBaseline(IGraphics.TextBaseline.Middle)
            val available = width - 2 * PADDING
            val text = elide(g, axisModel.name, available)
            elided = text != axisModel.name
            nameWidth = g.getStringWidth(axisModel.name).toDouble()
            val textWidth = g.getStringWidth(text)
            val x = when (alignment) {
                Alignment.Left -> PADDING
                Alignment.Right -> width - PADDING - textWidth
                else -> (width - textWidth) / 2
            }
            g.drawString(text, x.toFloat(), (height / 2).toFloat())
        }

        fun changed() {
            notifyIDrawingChanged()
        }
    }

    private val buttonListener = object : PropertyListener<Boolean> {
        override fun propertyChanged(event: PropertyEvent<Boolean>) {
            drawing.changed()
        }
    }

    private val mouseListener = object : MouseListener, MouseMotionListener {
        override fun mousePressed(e: MouseEvent) {
            if (!isInteractive) {
                return
            }
            e.stopPropagation()
            hideTooltip()
            if (e.isPopupTrigger) {
                if (!axisModel.buttonModel.value) {
                    axisModel.buttonModel.value = true
                    if (!e.isMultipleSelectionKey) {
                        view.deselectOtherHeaders(axisModel)
                    }
                }
                view.showHeaderPopupMenu(this@AxisHeaderView, e.x, e.y)
                return
            }
            if (!e.isMultipleSelectionKey) {
                view.deselectOtherHeaders(axisModel)
            }
            pressed = true
            dragged = false
            pressX = view.toViewX(this@AxisHeaderView, e.x)
            drawing.changed()
            view.getAxisView(axisModel)?.let { view.startDragging(it) }
        }

        override fun mouseDragged(e: MouseEvent) {
            if (!isInteractive) {
                return
            }
            e.stopPropagation()
            if (pressed) {
                val shift = view.toViewX(this@AxisHeaderView, e.x) - pressX
                if (abs(shift) > 2) {
                    dragged = true
                }
                if (dragged) {
                    view.getAxisView(axisModel)?.let { view.dragAxisTo(it, shift) }
                }
            }
        }

        override fun mouseReleased(e: MouseEvent) {
            if (!isInteractive) {
                return
            }
            e.stopPropagation()
            if (pressed) {
                pressed = false
                val axisView = view.getAxisView(axisModel)
                if (axisView != null) {
                    view.stopDragging(axisView)
                    if (!dragged) {
                        view.headerSelected(axisView, e.clickCount)
                    }
                }
                dragged = false
                axisModel.buttonModel.value = false
                drawing.changed()
            }
        }

        override fun mouseClicked(e: MouseEvent) {
            if (!isInteractive) {
                return
            }
            e.stopPropagation()
        }

        override fun mouseEntered(e: MouseEvent) {
            hover(e)
        }

        override fun mouseExited(e: MouseEvent) {
            hideTooltip()
        }

        override fun mouseMoved(e: MouseEvent) {
            if (!isInteractive) {
                return
            }
            e.stopPropagation()
            hover(e)
        }
    }

    private fun hover(e: MouseEvent) {
        if (!isInteractive) {
            return
        }
        hovered = true
        hoverX = e.x
        hoverY = e.y
        if (elided && !pressed) {
            tooltipTimer.restart()
        }
    }

    private fun hideTooltip() {
        hovered = false
        view.hideTooltip(this)
    }

    init {
        addLayer(drawing)
        addMouseListener(mouseListener)
        addMouseMotionListener(mouseListener)
        axisModel.buttonModel.addPropertyListener(buttonListener)
    }

    fun dispose() {
        axisModel.buttonModel.removePropertyListener(buttonListener)
    }

    companion object {
        /** Preferred height: the button insets around a line of its font. */
        const val HEIGHT = 22

        private const val PADDING = 3.0

        private val FACE: MkColor = colorOf(242, 242, 242)
        private val DOWN: MkColor = colorOf(200, 200, 200)
        private val BORDER: MkColor = colorOf(165, 165, 165)
        private val TEXT: MkColor = colorOf(0, 0, 0)

        internal val FONT: MkFont? by lazy { CPFontFactory.instance.createFont(null, Weight.NORMAL, 12) }

        /** [text], shortened with an ellipsis to fit in [available] pixels. */
        internal fun elide(g: IGraphics, text: String, available: Double): String {
            if (g.getStringWidth(text) <= available) {
                return text
            }
            var end = text.length
            while (end > 0 && g.getStringWidth(text.substring(0, end) + "…") > available) {
                end--
            }
            return if (end > 0) text.substring(0, end) + "…" else ""
        }
    }
}
